/**
 * HOOK: useCrews (VERSION REFACTORISÉE)
 * Utilise le hook useCRUD générique avec relations complexes
 *
 * 🎯 Avant: 277 lignes | Après: 115 lignes | Réduction: 58%
 */

import { useCallback, useMemo } from 'react';
import { CREW_CONFIG } from '../config/entities/crew.config';
import { useUsers } from '../contexts/UsersContext';
import { generateSearchableFields } from '../lib/entity-config';
import { crewNumberErrorMessage } from '../lib/crewNumberAvailability';
import { applyFiltersToQuery } from '../lib/supabase-filters';
import { buildCrewSearchConditions } from '../lib/postgrestSearch';
import { fetchAllPages, fetchInChunks } from '../lib/supabase-paginate';
import { fetchCrewsBedSummary } from '../lib/bed/admin';
import { resolveSortColumn, CREWS_FILTER_VIEW_SORT_COLUMNS } from '../lib/sort-columns';
import type { CreateCrewInput, Crew, UpdateCrewInput } from '../types/crew';
import { useAuth } from './useAuth';
import { useCRUD } from './useCRUD';
import { useSupabase } from './useSupabase';

interface CreatedCrewReference {
  id: string;
  crew_number: string | null;
  product_id: string | null;
}

/**
 * Type brut retourné par Supabase avec toutes les relations
 */
interface CrewRaw {
  id: string;
  name: string;
  crew_number?: string;
  status: string;
  probability: string;
  budget?: number;
  assigned_to?: string;
  product_id?: string;
  vehicle_id?: string;
  association_id?: string;
  discount_id?: string;
  financing_type: string;
  has_derogation: boolean;
  derogation_pending: boolean;
  organizes_pvd: boolean;
  has_vehicle: boolean;
  report?: string;
  notes?: string;
  organization_id: string;
  created_at: string;
  updated_at: string;
  product?: { id: string; name: string };
  vehicle?: { id: string; registration: string };
  association?: { id: string; name: string };
  discount?: { id: string; name: string; amount: number };
  members?: Array<{
    role?: string | null;
    contact: {
      id: string;
      first_name: string;
      last_name: string;
      email: string;
      phone?: string;
      birth_date?: string | null;
    };
  }>;
}

interface UseCrewsOptions {
  /**
   * Mode de chargement
   * - 'client': Charge toutes les données au montage (défaut)
   * - 'server': Ne charge pas au montage (utiliser fetchPaginated)
   */
  mode?: 'client' | 'server';
}

const CREW_FILTER_VIEW_FIELDS = new Set([
  'member_names',
  'member_ids',
  'call_count',
  'pilot_postal_code',
  'pilot_city',
  'pilot_full_name',
  'bpi_code',
]);

function hasCrewFilterViewField(filters?: Array<{ field?: string }> | null): boolean {
  return Boolean(
    filters?.some((filter) => filter.field && CREW_FILTER_VIEW_FIELDS.has(filter.field))
  );
}

export function useCrews(options: UseCrewsOptions = {}) {
  const { mode = 'client' } = options;
  const supabase = useSupabase();
  const { user } = useAuth();
  const { usersMap } = useUsers();

  // Memoïser les searchableFields pour éviter les re-renders
  const searchableFields = useMemo(() => generateSearchableFields(CREW_CONFIG), []);

  const crud = useCRUD<Crew, CreateCrewInput, UpdateCrewInput, CrewRaw>('crews', {
    select: `
      *,
      product:products(id, name, start_date),
      vehicle:vehicles(id, registration),
      association:associations(id, name),
      discount:discounts(id, name, amount),
      members:crew_members(
        role,
        derogation_validated,
        contact:contacts(id, first_name, last_name, email, phone, postal_code, city, birth_date)
      )
    `,
    orderBy: { column: 'created_at', ascending: false },
    manual: mode === 'server', // Désactiver le fetch automatique en mode server
    searchableFields,

    // Transformer les données brutes en Crew typé
    transform: (raw): Crew => ({
      id: raw.id,
      name: raw.name,
      crew_number: raw.crew_number || null,
      status: raw.status as any,
      probability: raw.probability as any,
      budget: raw.budget || null,
      assigned_to: raw.assigned_to || null,
      product_id: raw.product_id || null,
      vehicle_id: raw.vehicle_id || null,
      association_id: raw.association_id || null,
      discount_id: raw.discount_id || null,
      financing_type: raw.financing_type as any,
      has_derogation: raw.has_derogation,
      derogation_pending: (raw as any).derogation_pending ?? false,
      organizes_pvd: raw.organizes_pvd,
      has_vehicle: raw.has_vehicle ?? false,
      report: raw.report || null,
      notes: raw.notes || null,
      created_at: raw.created_at,
      updated_at: raw.updated_at,
      // Relations
      product: raw.product || null,
      vehicle: raw.vehicle || null,
      association: raw.association || null,
      discount: raw.discount || null,
      // Aplatir la structure des membres - inclure le rôle (cast en ContactWithRole[])
      members: (raw.members
        ?.map((m: any) => ({
          ...m.contact,
          role: m.role,
          derogation_validated: m.derogation_validated ?? false,
        }))
        .filter(Boolean) || []) as any,
      // assigned_user et call_count seront enrichis après le fetch
      assigned_user: null,
      call_count: 0,
    }),

    // Hook appelé après chaque fetch pour enrichir les données
    afterFetch: async (crews: Crew[]) => {
      if (crews.length === 0) return crews;

      // 1. Enrichir avec assigned_user depuis le cache UsersContext
      const enrichedCrews = crews.map((crew) => {
        if (!crew.assigned_to) return crew;
        const assignedUser = usersMap[crew.assigned_to];
        return { ...crew, assigned_user: assignedUser || null };
      });

      // 2. Récupérer tous les contact_ids des membres de tous les équipages
      const allContactIds = new Set<string>();
      enrichedCrews.forEach((crew) => {
        crew.members?.forEach((member: any) => {
          if (member?.id) allContactIds.add(member.id);
        });
      });

      if (allContactIds.size === 0) return enrichedCrews;

      // 3. Récupérer tous les appels pour ces contacts (pour compter)
      const callEvents = await fetchInChunks<any>(Array.from(allContactIds), 500, async (chunk) => {
        const { data, error } = await supabase
          .from('timeline_events')
          .select('entity_id')
          .eq('entity_type', 'contact')
          .eq('event_type', 'call_logged')
          .eq('metadata->>outcome', 'answered')
          .in('entity_id', chunk);
        if (error) throw error;
        return data || [];
      });

      // Compter les appels par contact
      const callCountByContact = new Map<string, number>();
      for (const call of callEvents) {
        const count = callCountByContact.get(call.entity_id) || 0;
        callCountByContact.set(call.entity_id, count + 1);
      }

      // 4. Calculer le call_count de chaque équipage = somme des appels de tous ses membres
      return enrichedCrews.map((crew) => {
        let totalCalls = 0;

        crew.members?.forEach((member: any) => {
          const memberCallCount = callCountByContact.get(member?.id) || 0;
          totalCalls += memberCallCount;
        });

        return { ...crew, call_count: totalCalls };
      });
    },

    // Nettoyer les chaînes vides pour les UUID optionnels
    transformInput: (input) => {
      const cleaned: any = { ...input };
      if (cleaned.product_id === '') cleaned.product_id = null;
      if (cleaned.vehicle_id === '') cleaned.vehicle_id = null;
      if (cleaned.association_id === '') cleaned.association_id = null;
      if (cleaned.discount_id === '') cleaned.discount_id = null;
      if (cleaned.assigned_to === '') cleaned.assigned_to = null;
      return cleaned;
    },

    // Activer le logging automatique
    enableAutoLogging: true,
    entityType: 'crew',
    getUserId: () => user?.id,
  });

  /**
   * Enrichir un crew avec les données de l'utilisateur assigné
   * Utilise le cache du UsersContext
   */
  async function enrichWithAssignedUser(crew: Crew): Promise<Crew> {
    if (!crew.assigned_to) return crew;

    // Utiliser le cache du UsersContext (pas de requête réseau)
    const assignedUser = usersMap[crew.assigned_to];
    return { ...crew, assigned_user: assignedUser || null };
  }

  /**
   * Récupérer un équipage par ID avec l'utilisateur assigné
   */
  async function getCrewById(id: string): Promise<Crew | null> {
    const crew = await crud.getById(id);
    if (!crew) return null;
    return enrichWithAssignedUser(crew);
  }

  /**
   * Créer un équipage
   */
  async function createCrew(input: CreateCrewInput): Promise<Crew | null> {
    const newCrew = await crud.create(input);
    if (!newCrew) return null;
    return enrichWithAssignedUser(newCrew);
  }

  /**
   * Création utilisée par le modal CRM-16. Le numéro vide est alloué et le
   * crew est inséré dans la même transaction côté PostgreSQL.
   */
  async function createCrewWithNumberAssist(
    input: CreateCrewInput,
    allowReserved = false
  ): Promise<CreatedCrewReference> {
    const nullableUuid = (value: string | undefined) => (value && value !== '' ? value : null);
    const budget = input.budget;
    const { data, error } = await supabase
      .rpc('create_crew_with_number_assist', {
        p_name: input.name ?? null,
        p_crew_number: input.crew_number ?? null,
        p_product_id: nullableUuid(input.product_id),
        p_status: input.status ?? 'pre_registered',
        p_probability: input.probability,
        p_vehicle_id: nullableUuid(input.vehicle_id),
        p_association_id: nullableUuid(input.association_id),
        p_discount_id: nullableUuid(input.discount_id),
        p_assigned_to: nullableUuid(input.assigned_to),
        p_financing_type: input.financing_type ?? 'self_financing',
        p_budget: typeof budget === 'number' && !Number.isNaN(budget) ? budget : null,
        p_report: input.report ?? null,
        p_has_derogation: input.has_derogation ?? false,
        p_derogation_pending: input.derogation_pending ?? false,
        p_organizes_pvd: input.organizes_pvd ?? false,
        p_has_vehicle: input.has_vehicle ?? false,
        p_notes: input.notes ?? null,
        p_allow_reserved: allowReserved,
      })
      .single();

    if (error) {
      const message =
        error.code === '23505'
          ? 'Ce numéro vient d’être attribué à un autre équipage. Utilisez « Prochain numéro dispo ». '
          : crewNumberErrorMessage(error.message);
      throw new Error(message.trim());
    }

    const created = data as {
      crew_id?: string;
      crew_number?: string | null;
      product_id?: string | null;
    } | null;
    if (!created?.crew_id) throw new Error("L'équipage a été créé sans identifiant retourné.");

    return {
      id: created.crew_id,
      crew_number: created.crew_number ?? null,
      product_id: created.product_id ?? null,
    };
  }

  /**
   * Ajouter un membre à un équipage
   */
  async function addCrewMember(crewId: string, contactId: string): Promise<boolean> {
    try {
      const { error: insertError } = await supabase
        .from('crew_members')
        .insert([{ crew_id: crewId, contact_id: contactId }]);

      if (insertError) throw insertError;

      await crud.refresh();
      return true;
    } catch (err) {
      console.error('Error adding crew member:', err);
      return false;
    }
  }

  /**
   * Retirer un membre d'un équipage
   */
  async function removeCrewMember(crewId: string, contactId: string): Promise<boolean> {
    try {
      const { error: deleteError } = await supabase
        .from('crew_members')
        .delete()
        .eq('crew_id', crewId)
        .eq('contact_id', contactId);

      if (deleteError) throw deleteError;

      await crud.refresh();
      return true;
    } catch (err) {
      console.error('Error removing crew member:', err);
      return false;
    }
  }

  /**
   * Mettre à jour un équipage
   * Le logging est maintenant géré automatiquement par useCRUD
   */
  async function updateCrew(id: string, input: UpdateCrewInput): Promise<boolean> {
    return await crud.update(id, input);
  }

  /**
   * Retirer l'assignation d'un équipage
   */
  async function unassignUser(id: string): Promise<boolean> {
    try {
      // Récupérer l'équipage actuel pour logger l'ancien utilisateur
      const crew = await getCrewById(id);
      if (!crew) return false;

      const oldUserId = crew.assigned_to;
      const oldUserName = crew.assigned_user?.full_name || crew.assigned_user?.email || null;

      // Mettre à jour l'équipage
      const success = await crud.update(id, { assigned_to: null } as any);
      if (!success) return false;

      // Logger dans la timeline
      if (oldUserId) {
        try {
          await supabase.from('timeline_events').insert({
            entity_type: 'crew',
            entity_id: id,
            event_type: 'assigned_user_changed',
            title: 'Assignation retirée',
            description: `Assignation de ${oldUserName || 'utilisateur'} retirée`,
            created_by: user?.id || null,
            metadata: {
              old_user_id: oldUserId,
              old_user_name: oldUserName,
              new_user_id: null,
              new_user_name: null,
            },
          });
        } catch (logError) {
          console.error('Error logging timeline event:', logError);
        }
      }

      // Recharger les données
      await crud.refresh();
      return true;
    } catch (err) {
      console.error('Error unassigning user:', err);
      return false;
    }
  }

  /**
   * Récupérer les équipages associés à une entité
   */
  const getCrewsByEntityId = useCallback(
    async (entityType: 'vehicle' | 'association', entityId: string): Promise<Crew[]> => {
      try {
        const fieldMap = {
          vehicle: 'vehicle_id',
          association: 'association_id',
        };

        const field = fieldMap[entityType];

        const { data, error } = await supabase
          .from('crews')
          .select(
            `
          *,
          product:products(id, name, start_date),
          vehicle:vehicles(id, registration),
          association:associations(id, name),
          discount:discounts(id, name, amount),
          members:crew_members(
            role,
            derogation_validated,
            contact:contacts(id, first_name, last_name, email, phone, postal_code, city, birth_date)
          )
        `
          )
          .eq(field, entityId)
          .order('created_at', { ascending: false });

        if (error) throw error;

        // Transformer les données brutes en Crew typé
        return (data || []).map((raw: CrewRaw) => ({
          id: raw.id,
          name: raw.name,
          crew_number: raw.crew_number || null,
          status: raw.status as any,
          probability: raw.probability as any,
          budget: raw.budget || null,
          assigned_to: raw.assigned_to || null,
          product_id: raw.product_id || null,
          vehicle_id: raw.vehicle_id || null,
          association_id: raw.association_id || null,
          discount_id: raw.discount_id || null,
          financing_type: raw.financing_type as any,
          has_derogation: raw.has_derogation,
          derogation_pending: (raw as any).derogation_pending ?? false,
          organizes_pvd: raw.organizes_pvd,
          has_vehicle: raw.has_vehicle ?? false,
          report: raw.report || null,
          notes: raw.notes || null,
          created_at: raw.created_at,
          updated_at: raw.updated_at,
          // Relations
          product: raw.product || null,
          vehicle: raw.vehicle || null,
          association: raw.association || null,
          discount: raw.discount || null,
          // Aplatir la structure des membres - inclure le rôle
          members: (raw.members
            ?.map((m: any) => ({
              ...m.contact,
              role: m.role,
              derogation_validated: m.derogation_validated ?? false,
            }))
            .filter(Boolean) || []) as any,
          assigned_user: null,
        }));
      } catch (err) {
        console.error('Error fetching crews by entity:', err);
        return [];
      }
    },
    [supabase]
  );

  /**
   * Récupérer les équipages auxquels un contact appartient
   */
  const getCrewsByContactId = useCallback(
    async (contactId: string): Promise<Crew[]> => {
      try {
        const { data, error } = await supabase
          .from('crew_members')
          .select(
            `
          crew:crews(
            *,
            product:products(id, name, start_date),
            vehicle:vehicles(id, registration),
            association:associations(id, name),
            discount:discounts(id, name, amount),
            members:crew_members(
              role,
              derogation_validated,
              contact:contacts(id, first_name, last_name, email, phone, postal_code, city, birth_date)
            )
          )
        `
          )
          .eq('contact_id', contactId);

        if (error) throw error;

        // Extraire et transformer les crews
        const crews = (data || [])
          .map((item) => item.crew)
          .filter(Boolean)
          .map((raw: any) => ({
            id: raw.id,
            name: raw.name,
            crew_number: raw.crew_number || null,
            status: raw.status as any,
            probability: raw.probability as any,
            budget: raw.budget || null,
            assigned_to: raw.assigned_to || null,
            product_id: raw.product_id || null,
            vehicle_id: raw.vehicle_id || null,
            association_id: raw.association_id || null,
            discount_id: raw.discount_id || null,
            financing_type: raw.financing_type as any,
            has_derogation: raw.has_derogation,
            derogation_pending: (raw as any).derogation_pending ?? false,
            organizes_pvd: raw.organizes_pvd,
            has_vehicle: raw.has_vehicle ?? false,
            report: raw.report || null,
            notes: raw.notes || null,
            created_at: raw.created_at,
            updated_at: raw.updated_at,
            // Relations
            product: raw.product || null,
            vehicle: raw.vehicle || null,
            association: raw.association || null,
            discount: raw.discount || null,
            // Aplatir la structure des membres - inclure le rôle
            members: (raw.members
              ?.map((m: any) => ({
                ...m.contact,
                role: m.role,
                derogation_validated: m.derogation_validated ?? false,
              }))
              .filter(Boolean) || []) as any,
            assigned_user: null,
          }));

        return crews;
      } catch (err) {
        console.error('Error fetching crews by contact:', err);
        return [];
      }
    },
    [supabase]
  );

  /**
   * Récupérer TOUTES les données correspondant aux filtres (sans pagination)
   * Utilisé pour l'export CSV des données filtrées
   */
  const fetchFiltered = useCallback(
    async (params: import('../types/crud').FetchFilteredParams): Promise<Crew[]> => {
      try {
        const {
          search = '',
          filters,
          filterMode = 'and',
          requiredFilters,
          ids: requestedIds,
        } = params;

        let ids: string[];
        // Cache des lignes issues de la vue (pour propagation de pilot_postal_code, pilot_city)
        const filterViewMap = new Map<string, any>();

        // ✅ Si des IDs spécifiques sont demandés (export sélection multi-pages)
        if (requestedIds && requestedIds.length > 0) {
          ids = requestedIds;
        } else {
          const hasViewOnlyFilter =
            hasCrewFilterViewField(filters) || hasCrewFilterViewField(requiredFilters);

          // Choisir la source : vue si filtre nécessitant la vue, sinon table normale
          const hasViewSearchField =
            Boolean(search) &&
            (searchableFields as string[]).some((f) => CREW_FILTER_VIEW_FIELDS.has(f));
          const source = hasViewOnlyFilter || hasViewSearchField ? 'crews_filter_view' : 'crews';

          // R15 : factory de query (réinstancie à chaque page pour fetchAllPages)
          const buildQuery = () => {
            let query = supabase.from(source).select('*');

            // Recherche
            if (search && searchableFields && searchableFields.length > 0) {
              query = query.or(buildCrewSearchConditions(searchableFields as string[], search));
            }

            // Appliquer les filtres
            if (
              (filters && filters.length > 0) ||
              (requiredFilters && requiredFilters.length > 0)
            ) {
              query = applyFiltersToQuery(query, filters || [], filterMode, requiredFilters || []);
            }

            // Tri par défaut
            return query.order('created_at', { ascending: false });
          };

          // R15 : pagination automatique pour contourner le cap PostgREST 1000
          const { data: rawData, error: fetchError } = await fetchAllPages<any>(buildQuery);

          if (fetchError) throw fetchError;

          // Récupérer les IDs pour enrichir avec relations
          ids = rawData?.map((r: any) => r.id) || [];

          // Conserver les colonnes enrichies de la vue (pilot_postal_code, pilot_city, call_count)
          if (source === 'crews_filter_view' && rawData) {
            for (const row of rawData) {
              filterViewMap.set(row.id, row);
            }
          }
        }

        // Enrichir les données avec les relations
        let enrichedData: any[] = [];
        if (ids.length > 0) {
          // Bug fix : chunker pour éviter de dépasser la limite PostgREST (URL trop longue).
          const fullCrews = await fetchInChunks<any>(ids, 500, async (chunk) => {
            const { data, error } = await supabase
              .from('crews')
              .select(
                `
              *,
              product:products(id, name, start_date),
              vehicle:vehicles(id, registration),
              association:associations(id, name),
              discount:discounts(id, name, amount),
              members:crew_members(
                role,
                derogation_validated,
                contact:contacts(id, first_name, last_name, email, phone, postal_code, city, birth_date)
              )
            `
              )
              .in('id', chunk);
            if (error) throw error;
            return data || [];
          });

          if (fullCrews) {
            const crewMap = new Map(fullCrews.map((c) => [c.id, c]));
            enrichedData = ids
              .map((id: string) => {
                const crew = crewMap.get(id);
                if (!crew) return null;
                // Propager les colonnes enrichies de la vue si disponible
                const viewRow = filterViewMap.get(id);
                if (viewRow) {
                  return {
                    ...crew,
                    pilot_postal_code: viewRow.pilot_postal_code ?? null,
                    pilot_city: viewRow.pilot_city ?? null,
                    pilot_full_name: viewRow.pilot_full_name ?? null,
                    bpi_code: viewRow.bpi_code ?? null,
                  };
                }
                return crew;
              })
              .filter(Boolean);
          }
        }

        // Transformer les données - inclure le rôle dans les membres
        const transformedData = enrichedData.map((raw: any) => ({
          id: raw.id,
          name: raw.name,
          crew_number: raw.crew_number || null,
          status: raw.status,
          probability: raw.probability,
          budget: raw.budget || null,
          assigned_to: raw.assigned_to || null,
          product_id: raw.product_id || null,
          vehicle_id: raw.vehicle_id || null,
          association_id: raw.association_id || null,
          discount_id: raw.discount_id || null,
          financing_type: raw.financing_type,
          has_derogation: raw.has_derogation,
          derogation_pending: (raw as any).derogation_pending ?? false,
          organizes_pvd: raw.organizes_pvd,
          has_vehicle: raw.has_vehicle ?? false,
          report: raw.report || null,
          notes: raw.notes || null,
          created_at: raw.created_at,
          updated_at: raw.updated_at,
          product: raw.product || null,
          vehicle: raw.vehicle || null,
          association: raw.association || null,
          discount: raw.discount || null,
          members:
            raw.members
              ?.map((m: any) => ({
                ...m.contact,
                role: m.role,
                derogation_validated: m.derogation_validated ?? false,
              }))
              .filter(Boolean) || [],
          assigned_user:
            raw.assigned_to && usersMap[raw.assigned_to] ? usersMap[raw.assigned_to] : null,
          call_count: 0, // Sera enrichi ci-dessous
          // Item #4 : champs venant de la vue (null si non disponibles)
          pilot_postal_code: raw.pilot_postal_code ?? null,
          pilot_city: raw.pilot_city ?? null,
          // Bug 2 fix-tests-empiriques (R7) : nom complet pilote venant de la vue
          pilot_full_name: raw.pilot_full_name ?? null,
          bpi_code: raw.bpi_code ?? null,
        }));

        // Enrichir avec call_count des membres
        const allContactIds = new Set<string>();
        transformedData.forEach((crew: any) => {
          crew.members?.forEach((member: any) => {
            if (member?.id) allContactIds.add(member.id);
          });
        });

        if (allContactIds.size > 0) {
          // Bug fix : chunker pour éviter de dépasser la limite PostgREST (URL trop longue).
          const callEvents = await fetchInChunks<any>(
            Array.from(allContactIds),
            500,
            async (chunk) => {
              const { data, error } = await supabase
                .from('timeline_events')
                .select('entity_id')
                .eq('entity_type', 'contact')
                .eq('event_type', 'call_logged')
                .eq('metadata->>outcome', 'answered')
                .in('entity_id', chunk);
              if (error) throw error;
              return data || [];
            }
          );

          // Compter les appels par contact
          const callCountByContact = new Map<string, number>();
          for (const call of callEvents) {
            const count = callCountByContact.get(call.entity_id) || 0;
            callCountByContact.set(call.entity_id, count + 1);
          }

          transformedData.forEach((crew: any) => {
            let totalCalls = 0;
            crew.members?.forEach((member: any) => {
              const memberCallCount = callCountByContact.get(member?.id) || 0;
              totalCalls += memberCallCount;
            });
            crew.call_count = totalCalls;
          });
        }

        // Synthèse BED (migration 00232, cahier L131-136) : éléments manquants
        // et reste à payer, pour les colonnes de l'export CSV.
        // Enrichissement fait ICI et pas dans `afterFetch` : `fetchFiltered`
        // n'est appelé que pour l'export (jeu borné), là où `afterFetch` couvre
        // chaque affichage de liste — recalculer l'instantané BED de tous les
        // équipages à chaque rendu de page serait payé pour rien.
        // Un utilisateur non-staff reçoit une Map vide (la RPC est gardée) :
        // l'export part quand même, simplement sans les colonnes BED.
        const bedSummary = await fetchCrewsBedSummary(
          transformedData.map((crew: { id: string }) => crew.id)
        );
        transformedData.forEach(
          (
            crew: Pick<Crew, 'id'> &
              Partial<
                Pick<Crew, 'bed_missing_count' | 'bed_missing_labels' | 'bed_remaining_cents'>
              >
          ) => {
            const summary = bedSummary.get(crew.id);
            crew.bed_missing_count = summary?.bed_missing_count ?? null;
            crew.bed_missing_labels = summary?.bed_missing_labels ?? null;
            crew.bed_remaining_cents = summary?.bed_remaining_cents ?? null;
          }
        );

        return transformedData as Crew[];
      } catch (error) {
        console.error('[fetchFiltered] Error:', error);
        throw error;
      }
    },
    [supabase, searchableFields, usersMap]
  );

  /**
   * Récupérer tous les IDs correspondant aux filtres actuels (sans pagination)
   * Utilisé pour "Sélectionner tous les résultats" (Feature 4.4)
   */
  const fetchAllFilteredIds = useCallback(
    async (params: import('../types/crud').FetchFilteredParams): Promise<string[]> => {
      try {
        const { search = '', filters, filterMode = 'and', requiredFilters } = params;

        const hasViewOnlyFilter =
          hasCrewFilterViewField(filters) || hasCrewFilterViewField(requiredFilters);

        // Choisir la source : vue si filtre nécessitant la vue, sinon table normale
        const hasViewSearchField =
          Boolean(search) &&
          (searchableFields as string[]).some((f) => CREW_FILTER_VIEW_FIELDS.has(f));
        const source = hasViewOnlyFilter || hasViewSearchField ? 'crews_filter_view' : 'crews';

        // R15 : factory de query (réinstancie à chaque page pour fetchAllPages).
        // Récupère TOUS les IDs via pagination PostgREST (cap implicite = 1000) ;
        // sans cette boucle .range(), la sélection globale était silencieusement
        // tronquée à 1000 IDs (parité avec fix useContacts.fetchAllFilteredIds).
        const buildQuery = () => {
          let query = supabase.from(source).select('id');

          // Recherche
          if (search && searchableFields && searchableFields.length > 0) {
            query = query.or(buildCrewSearchConditions(searchableFields as string[], search));
          }

          // Appliquer les filtres
          if ((filters && filters.length > 0) || (requiredFilters && requiredFilters.length > 0)) {
            query = applyFiltersToQuery(query, filters || [], filterMode, requiredFilters || []);
          }

          // Ordre stable requis par la pagination .range() de fetchAllPages :
          // sans ORDER BY, des IDs peuvent être omis/dupliqués au-delà de 1000.
          return query.order('id');
        };

        const { data, error } = await fetchAllPages<{ id: string }>(buildQuery);

        if (error) throw error;

        return data?.map((item) => item.id) || [];
      } catch (error) {
        console.error('[fetchAllFilteredIds] Error:', error);
        throw error;
      }
    },
    [supabase, searchableFields]
  );

  /**
   * Fetch paginé utilisant la vue crews_filter_view
   * pour permettre le filtrage sur les membres (relation many-to-many)
   */
  const fetchPaginatedFromView = useCallback(
    async (params: any) => {
      try {
        const {
          page = 1,
          limit = 10,
          search = '',
          sort,
          filters,
          filterMode = 'and',
          requiredFilters,
        } = params;

        // Retour réunion 07/07 (E) : on lit TOUJOURS crews_filter_view (au lieu de
        // ne basculer sur la vue que pour un filtre/tri/recherche enrichi). Sans
        // cela, la nouvelle colonne « N° BPI » (bpi_code, champ de la vue) était
        // vide au simple parcours de la liste (source = table `crews` de base).
        // La vue expose le superset des colonnes crews + agrégats, applique la
        // même RLS (security_invoker) et la liste est paginée (25/pg) → coût
        // négligeable. Les blocs `source === 'crews_filter_view'` ci-dessous
        // (enrichissement) restent valides.
        const source = 'crews_filter_view';

        // Construire la requête
        let query = supabase.from(source).select('*', { count: 'exact', head: false });

        // Recherche
        if (search && searchableFields && searchableFields.length > 0) {
          query = query.or(buildCrewSearchConditions(searchableFields as string[], search));
        }

        // Appliquer les filtres
        if ((filters && filters.length > 0) || (requiredFilters && requiredFilters.length > 0)) {
          query = applyFiltersToQuery(query, filters || [], filterMode, requiredFilters || []);
        }

        // Tri — whitelist serveur : un champ hors colonnes reelles de la vue
        // (relations enrichies cote client : `members`, `product`, `vehicle`...)
        // rabat sur created_at pour eviter un 400 PostgREST (issue #230).
        const sortColumn = resolveSortColumn(sort?.field, CREWS_FILTER_VIEW_SORT_COLUMNS);
        const sortAscending = sort?.direction === 'asc';
        query = query.order(sortColumn, { ascending: sortAscending });

        // Pagination
        const from = (page - 1) * limit;
        const to = from + limit - 1;
        query = query.range(from, to);

        // Exécution
        const { data: rawData, error: fetchError, count } = await query;

        if (fetchError) throw fetchError;

        // Récupérer les IDs pour enrichir avec relations
        const ids = rawData?.map((r: any) => r.id) || [];

        // Map des données enrichies issues de la vue (call_count, pilot_postal_code, pilot_city)
        const viewDataMap = new Map<string, any>();
        if (source === 'crews_filter_view' && rawData) {
          for (const row of rawData) {
            viewDataMap.set(row.id, row);
          }
        }

        let enrichedData = rawData || [];
        if (ids.length > 0) {
          // Récupérer les crews complets avec relations (incluant le rôle des membres)
          const { data: fullCrews } = await supabase
            .from('crews')
            .select(
              `
              *,
              product:products(id, name, start_date),
              vehicle:vehicles(id, registration),
              association:associations(id, name),
              discount:discounts(id, name, amount),
              members:crew_members(
                role,
                derogation_validated,
                contact:contacts(id, first_name, last_name, email, phone, postal_code, city, birth_date)
              )
            `
            )
            .in('id', ids);

          if (fullCrews) {
            const crewMap = new Map(fullCrews.map((c) => [c.id, c]));
            enrichedData = ids
              .map((id: string) => {
                const crew = crewMap.get(id);
                if (!crew) return null;
                // Si on a interrogé la vue, propager les colonnes enrichies (pilot_postal_code, pilot_city)
                const viewRow = viewDataMap.get(id);
                if (viewRow) {
                  return {
                    ...crew,
                    pilot_postal_code: viewRow.pilot_postal_code ?? null,
                    pilot_city: viewRow.pilot_city ?? null,
                    pilot_full_name: viewRow.pilot_full_name ?? null,
                    bpi_code: viewRow.bpi_code ?? null,
                  };
                }
                return crew;
              })
              .filter(Boolean);
          }
        }

        // Transformer les données - inclure le rôle dans les membres
        const transformedData = enrichedData.map((raw: any) => ({
          id: raw.id,
          name: raw.name,
          crew_number: raw.crew_number || null,
          status: raw.status,
          probability: raw.probability,
          budget: raw.budget || null,
          assigned_to: raw.assigned_to || null,
          product_id: raw.product_id || null,
          vehicle_id: raw.vehicle_id || null,
          association_id: raw.association_id || null,
          discount_id: raw.discount_id || null,
          financing_type: raw.financing_type,
          has_derogation: raw.has_derogation,
          derogation_pending: (raw as any).derogation_pending ?? false,
          organizes_pvd: raw.organizes_pvd,
          has_vehicle: raw.has_vehicle ?? false,
          report: raw.report || null,
          notes: raw.notes || null,
          created_at: raw.created_at,
          updated_at: raw.updated_at,
          product: raw.product || null,
          vehicle: raw.vehicle || null,
          association: raw.association || null,
          discount: raw.discount || null,
          members:
            raw.members
              ?.map((m: any) => ({
                ...m.contact,
                role: m.role,
                derogation_validated: m.derogation_validated ?? false,
              }))
              .filter(Boolean) || [],
          assigned_user:
            raw.assigned_to && usersMap[raw.assigned_to] ? usersMap[raw.assigned_to] : null,
          call_count: 0, // Sera enrichi ci-dessous
          // Item #4 : champs venant de la vue (null si non disponibles)
          pilot_postal_code: raw.pilot_postal_code ?? null,
          pilot_city: raw.pilot_city ?? null,
          // Bug 2 fix-tests-empiriques (R7) : nom complet pilote venant de la vue
          pilot_full_name: raw.pilot_full_name ?? null,
          bpi_code: raw.bpi_code ?? null,
        }));

        // Enrichir avec call_count des membres
        const allContactIds = new Set<string>();
        transformedData.forEach((crew: any) => {
          crew.members?.forEach((member: any) => {
            if (member?.id) allContactIds.add(member.id);
          });
        });

        if (allContactIds.size > 0) {
          const callEvents = await fetchInChunks<any>(
            Array.from(allContactIds),
            500,
            async (chunk) => {
              const { data, error } = await supabase
                .from('timeline_events')
                .select('entity_id')
                .eq('entity_type', 'contact')
                .eq('event_type', 'call_logged')
                .eq('metadata->>outcome', 'answered')
                .in('entity_id', chunk);
              if (error) throw error;
              return data || [];
            }
          );

          // Compter les appels par contact
          const callCountByContact = new Map<string, number>();
          for (const call of callEvents) {
            const count = callCountByContact.get(call.entity_id) || 0;
            callCountByContact.set(call.entity_id, count + 1);
          }

          transformedData.forEach((crew: any) => {
            let totalCalls = 0;
            crew.members?.forEach((member: any) => {
              const memberCallCount = callCountByContact.get(member?.id) || 0;
              totalCalls += memberCallCount;
            });
            crew.call_count = totalCalls;
          });
        }

        return {
          data: transformedData as Crew[],
          total: count || 0,
          totalPages: Math.ceil((count || 0) / limit),
          page,
          limit,
        };
      } catch (error) {
        console.error('[fetchPaginatedFromView] Error:', error);
        throw error;
      }
    },
    [supabase, searchableFields, usersMap]
  );

  return {
    // API spécifique (pour compatibilité)
    crews: crud.data,
    loading: crud.loading,
    error: crud.error,
    refreshCrews: crud.refresh,
    getCrewById,
    createCrew,
    createCrewWithNumberAssist,
    updateCrew,
    deleteCrew: crud.remove,
    deleteCrews: crud.removeMany,
    unassignUser,
    getCrewsByEntityId,
    getCrewsByContactId,

    // API générique (pour useEntityList)
    data: crud.data,
    allData: crud.data, // Toutes les données (pour Import CSV en mode server)
    create: createCrew,
    createWithNumberAssist: createCrewWithNumberAssist,
    update: updateCrew,
    remove: crud.remove,
    removeMany: crud.removeMany,
    refresh: crud.refresh, // Retourne T[] maintenant
    refreshAll: crud.refresh, // Alias explicite
    fetchPaginated: fetchPaginatedFromView, // ✅ Utilise la vue pour supporter le filtre membres
    fetchFiltered, // ✅ Pour export CSV des données filtrées (sans pagination)
    fetchAllFilteredIds, // ✅ Pour sélection globale (Feature 4.4)

    // Gestion des membres
    addCrewMember,
    removeCrewMember,
  };
}

/**
 * MIGRATION GUIDE:
 *
 * ✅ API 100% COMPATIBLE - Aucun changement nécessaire dans les composants !
 *
 * Le hook expose exactement les mêmes méthodes que l'ancienne version:
 * - crews, loading, error
 * - fetchCrews(), getCrewById(), createCrew(), updateCrew(), deleteCrew(), deleteCrews()
 * - addCrewMember(), removeCrewMember()
 *
 * 🎯 Réduction de 58% du code tout en gardant toutes les fonctionnalités
 */
